import React, { useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import KeycloakContext from "../../keycloakContext";
import { fetchCurrentSurveyData, fetchSurveyData } from "../../api_calls";
import {
  clearProfessorClassicFormCache,
  getClassicSurveyDraftId,
  hydrateActiveProfessorClassicSurveyDraft,
  hydrateProfessorClassicSurveyDraft,
} from "../utils/classicSurveyDraft.js";
import { getClassicSurveyProgress } from "../utils/classicSurveyProgress.js";

function isProfessorUser(keycloak) {
  const groups = keycloak?.tokenParsed?.groups || [];
  return groups.includes("/professor") || groups.includes("professor");
}

function isAdminUser(keycloak) {
  const groups = keycloak?.tokenParsed?.groups || [];
  return groups.includes("/admin") || groups.includes("admin");
}

function getUsername(keycloak) {
  return keycloak?.tokenParsed?.preferred_username || "";
}

function getClassicBlockIdFromPath(pathname = "") {
  const match = pathname.match(/\/bloque\/(\d+)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 7 ? parsed : null;
}

export function ProfessorClassicBlockGate({ children }) {
  const keycloak = useContext(KeycloakContext);
  const location = useLocation();
  const requestedBlockId = getClassicBlockIdFromPath(location.pathname);
  const [state, setState] = useState({ status: "loading", message: "" });
  const [redirectTo, setRedirectTo] = useState(null);
  const username = getUsername(keycloak);

  useEffect(() => {
    let cancelled = false;

    async function hydrateActiveSurvey() {
      if (!isProfessorUser(keycloak)) {
        setState({ status: "ready", message: "" });
        return;
      }

      try {
        const surveyId = getClassicSurveyDraftId(username);
        let survey = null;

        if (surveyId) {
          survey = await hydrateActiveProfessorClassicSurveyDraft(username, surveyId);
        } else {
          try {
            survey = await fetchCurrentSurveyData();
          } catch (error) {
            if (error?.response?.status !== 404) throw error;
          }
          if (survey?._id) {
            hydrateProfessorClassicSurveyDraft(username, survey);
          } else {
            clearProfessorClassicFormCache(username, null);
          }
        }

        const progress = getClassicSurveyProgress(survey || {});
        if (requestedBlockId != null && !progress.unlockedBlocks.includes(requestedBlockId)) {
          if (!cancelled) setRedirectTo("/applicatiu");
          return;
        }

        if (!cancelled) setState({ status: "ready", message: "" });
      } catch (error) {
        console.error("Could not hydrate active professor survey", error);
        if (!cancelled) setState({ status: "error", message: "No s'ha pogut carregar l'enquesta activa." });
      }
    }

    hydrateActiveSurvey();
    return () => {
      cancelled = true;
    };
  }, [keycloak, requestedBlockId, username]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (state.status === "loading") return <div className="p-6 text-center">Carregant enquesta...</div>;
  if (state.status === "error") return <div className="p-6 text-center text-red-600">{state.message}</div>;
  return children;
}

export function ProfessorEditRouteGate({ children }) {
  const keycloak = useContext(KeycloakContext);
  const { id } = useParams();
  const username = getUsername(keycloak);
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(isProfessorUser(keycloak));

  useEffect(() => {
    let cancelled = false;

    async function resolveProfessorRoute() {
      if (!isProfessorUser(keycloak)) {
        setLoading(false);
        return;
      }

      try {
        const survey = await fetchSurveyData(id);
        if (survey?.username === username) {
          hydrateProfessorClassicSurveyDraft(username, survey);
          setRedirectTo(`/bloque/${getClassicSurveyProgress(survey).resumeBlockId}`);
        } else {
          setRedirectTo(`/survey/${id}/view`);
        }
      } catch (error) {
        console.error("Could not resolve professor edit route", error);
        setRedirectTo("/applicatiu");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolveProfessorRoute();
    return () => {
      cancelled = true;
    };
  }, [id, keycloak, username]);

  if (isAdminUser(keycloak)) return children;
  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (loading) return <div className="p-6 text-center">Carregant enquesta...</div>;
  return children;
}

export function ProfessorEditBlockRouteGate({ children }) {
  const keycloak = useContext(KeycloakContext);
  const { surveyId, blockId } = useParams();
  const username = getUsername(keycloak);
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(isProfessorUser(keycloak));

  useEffect(() => {
    let cancelled = false;

    async function resolveProfessorBlockRoute() {
      if (!isProfessorUser(keycloak)) {
        setLoading(false);
        return;
      }

      try {
        const survey = await fetchSurveyData(surveyId);
        if (survey?.username === username) {
          hydrateProfessorClassicSurveyDraft(username, survey);
          setRedirectTo(`/bloque/${blockId}`);
        } else {
          setRedirectTo(`/survey/${surveyId}/view`);
        }
      } catch (error) {
        console.error("Could not resolve professor edit block route", error);
        setRedirectTo("/applicatiu");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolveProfessorBlockRoute();
    return () => {
      cancelled = true;
    };
  }, [blockId, keycloak, surveyId, username]);

  if (isAdminUser(keycloak)) return children;
  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (loading) return <div className="p-6 text-center">Carregant enquesta...</div>;
  return children;
}
